import { useState, useEffect, useRef } from "react";
import { Phone } from "react-feather";
import styles from "./identityVoice.module.css";

const IdentityVoiceCallBar = ({
  call,
  callPartnerName,
  callPartnerIdentity,
  callPartnerAvatar,
  onHangup,
  onCallEnded,
}) => {
  const [callStatus, setCallStatus] = useState("connecting");
  const [durationSeconds, setDurationSeconds] = useState(0);
  const wasAcceptedRef = useRef(false);
  const durationRef = useRef(0);
  const callStatusRef = useRef("connecting");

  // Always keep the latest callback versions available, so the
  // listener effect (keyed only on [call]) never uses a stale closure
  const onCallEndedRef = useRef(onCallEnded);
  const onHangupRef = useRef(onHangup);
  useEffect(() => {
    onCallEndedRef.current = onCallEnded;
  }, [onCallEnded]);
  useEffect(() => {
    onHangupRef.current = onHangup;
  }, [onHangup]);

  useEffect(() => {
    if (!call) return;
    setCallStatus("connecting");
    callStatusRef.current = "connecting";
    setDurationSeconds(0);
    durationRef.current = 0;
    wasAcceptedRef.current = false;
    console.log("[CALLBAR] reset for new call", call?.parameters);
  }, [call]);

  useEffect(() => {
    if (!call) return;

    const handleRinging = () => {
      callStatusRef.current = "ringing";
      setCallStatus("ringing");
    };
    const handleAccept = () => {
      wasAcceptedRef.current = true;
      callStatusRef.current = "accept";
      setCallStatus("accept");
    };
    const handleDisconnect = () => {
      callStatusRef.current = "disconnect";
      setCallStatus("disconnect");
      const outcome = wasAcceptedRef.current ? "completed" : "no-answer";
      onCallEndedRef.current &&
        onCallEndedRef.current(outcome, durationRef.current);
      onHangupRef.current && onHangupRef.current();
    };
    const handleError = (error) => {
      console.log(
        "[IDENTITY VOICE] call error",
        error,
        "callStatus at error:",
        callStatusRef.current
      );
      callStatusRef.current = "disconnect";
      setCallStatus("disconnect");
      let outcome = wasAcceptedRef.current ? "completed" : "no-answer";
      onCallEndedRef.current &&
        onCallEndedRef.current(outcome, durationRef.current);
      onHangupRef.current && onHangupRef.current();
    };

    call.on("ringing", handleRinging);
    call.on("accept", handleAccept);
    call.on("disconnect", handleDisconnect);
    call.on("error", handleError);

    return () => {
      call.off("ringing", handleRinging);
      call.off("accept", handleAccept);
      call.off("disconnect", handleDisconnect);
      call.off("error", handleError);
    };
  }, [call]);

  useEffect(() => {
    if (callStatus !== "accept") return;
    const interval = setInterval(() => {
      setDurationSeconds((prev) => {
        const next = prev + 1;
        durationRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [callStatus]);

  const formatDuration = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    const first = parts[0] ? parts[0].charAt(0).toUpperCase() : "";
    const last = parts[parts.length - 1] ? parts[parts.length - 1].charAt(0).toUpperCase() : "";
    return first + last || "?";
  };

  if (!call || callStatus === "disconnect") return null;

  return (
    <div className={`${styles.CallBar} CallBar`}>
      <div className={`${styles.CallBarAvatar}`}>
        {callPartnerAvatar && callPartnerAvatar !== "" ? (
          <img src={callPartnerAvatar} alt="" />
        ) : (
          <p>{getInitials(callPartnerName)}</p>
        )}
      </div>
      <div className={`${styles.CallBarDetails}`}>
        {callPartnerName && (
          <p className={`${styles.Name}`}>{callPartnerName}</p>
        )}
        <p
          className={`${styles.StatusText}`}
          style={{
            color: callStatus === "accept" ? "#27c27b" : "#eaa043",
          }}
        >
          {callStatus === "connecting" && "Connecting…"}
          {callStatus === "ringing" && "Ringing…"}
          {callStatus === "accept" && formatDuration(durationSeconds)}
        </p>
      </div>
      <button
        className={`${styles.HangupBU}`}
        onClick={() => call.disconnect()}
      >
        <Phone />
      </button>
    </div>
  );
};

export default IdentityVoiceCallBar;
